import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import Aurora from '../../components/Aurora/Aurora';
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    const [navbarHidden, setNavbarHidden] = useState(false);
    const lastScrollY = useRef(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const viewportHeight = window.innerHeight;

            if (currentScrollY > lastScrollY.current && currentScrollY > viewportHeight) {
                setNavbarHidden(true);
            } else {
                setNavbarHidden(false);
            }
            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="privacy-policy-page">
            <Navbar className={navbarHidden ? 'hidden' : ''} />

            {/* Aurora header */}
            <div className="privacy-aurora hero-section">
                <div className="aurora-wrapper">
                    <Aurora
                        colorStops={["#0088FF", "#4ecdc4", "#1E40AF"]}
                        blend={0.8}
                        amplitude={1.2}
                        speed={0.3}
                    />
                </div>
            </div>

            <div className="privacy-content">
                <div className="privacy-container">
                    <header className="privacy-header">
                        <h1 className="section-title">Privacy Policy</h1>
                        <p>Your privacy is important to us. Last updated: January 2026</p>
                    </header>

                    <div className="privacy-card">
                        <section className="privacy-section">
                            <h2>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                </svg>
                                1. Information We Collect
                            </h2>
                            <p>My Digital Schools collects information to provide better services to all our users. We collect information in the following ways:</p>
                            <ul>
                                <li><strong>Information you give us:</strong> This includes personal information provided during account creation, such as your name, email address, school affiliation, and role (student, teacher, or admin).</li>
                                <li><strong>Information we get from your use of our services:</strong> We may collect information about the services that you use and how you use them, such as when you view and interact with content.</li>
                                <li><strong>SSO Data:</strong> When you use our Single Sign-On (SSO) features to access educational provider content, we transmit necessary authentication headers securely.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                2. How We Use Information
                            </h2>
                            <p>We use the information we collect from all of our services to provide, maintain, protect and improve them, to develop new ones, and to protect My Digital Schools and our users.</p>
                            <p>We specifically use your data to facilitate seamless SSO login to third-party educational providers, ensuring you don't need to manage multiple passwords.</p>
                        </section>

                        <section className="privacy-section">
                            <h2>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                </svg>
                                3. Information Sharing
                            </h2>
                            <p>We do not share personal information with companies, organizations and individuals outside of My Digital Schools unless one of the following circumstances applies:</p>
                            <ul>
                                <li><strong>With your consent:</strong> We will share personal information with companies, organizations or individuals outside of My Digital Schools when we have your consent to do so.</li>
                                <li><strong>For school administrators:</strong> Your domain administrator and resellers who provide user support to your organization will have access to your My Digital Schools account information.</li>
                                <li><strong>For legal reasons:</strong> We will share personal information with companies, organizations or individuals outside of My Digital Schools if we have a good-faith belief that access, use, preservation or disclosure of the information is reasonably necessary to meet any applicable law, regulation, legal process or enforceable governmental request.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                </svg>
                                4. Data Security
                            </h2>
                            <p>We work hard to protect My Digital Schools and our users from unauthorized access to or unauthorized alteration, disclosure or destruction of information we hold. In particular:</p>
                            <ul>
                                <li>We encrypt many of our services using SSL.</li>
                                <li>We review our information collection, storage and processing practices, including physical security measures, to guard against unauthorized access to systems.</li>
                                <li>We restrict access to personal information to My Digital Schools employees, contractors and agents who need to know that information in order to process it for us, and who are subject to strict contractual confidentiality obligations and may be disciplined or terminated if they fail to meet these obligations.</li>
                            </ul>
                        </section>
                    </div>
                </div>
            </div>
            <Footer />
            <ScrollToTop />
        </div>
    );
};

export default PrivacyPolicy;
